"use client";

import React, { useEffect, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { cn } from "@/lib/utils";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const categories = [{
  id: "baubegleitung",
  title: "Baubegleitung",
  imageSrc: "https://balanced-monster.10web.cloud/wp-content/uploads/2026/05/ChatGPT-Image-12.-Mai-2026-20_19_26.png?v=iqffjaiaxxg",
  imageSrcset: "https://balanced-monster.10web.cloud/wp-content/uploads/2026/05/ChatGPT-Image-12.-Mai-2026-20_19_26.png?v=dad1efp3ecd-wvc-srcset",
  imageSizes: "https://balanced-monster.10web.cloud/wp-content/uploads/2026/05/ChatGPT-Image-12.-Mai-2026-20_19_26.png?v=bmp6x3vfv36-wvc-sizes",
  imageAlt: "Professionelle Baubegleitung auf einer Baustelle in Hamburg"
}];
const services = [{
  categoryId: "baubegleitung",
  title: "Festlegung und Kontrolle von Schutzmaßnahmen gemäß DIN 18920",
  description: ""
}, {
  categoryId: "baubegleitung",
  title: "Begleitung von Erd-, Tiefbau-, Leitungs- und Baugrubenarbeiten in Baumnähe",
  description: ""
}, {
  categoryId: "baubegleitung",
  title: "Fachliche Beurteilung von Wurzeln und Entwicklung wurzelschonender Lösungen bei Eingriffen im Wurzelraum",
  description: ""
}, {
  categoryId: "baubegleitung",
  title: "Saugbaggerarbeiten for wurzelschonenden Aushub und Bodenauflockerung",
  description: ""
}, {
  categoryId: "baubegleitung",
  title: "Abstimmung mit Bauleitung und ausführenden Firmen sowie schnelle Unterstützung bei unvorhergesehenen Situationen auf der Baustelle",
  description: ""
}, {
  categoryId: "baumschutz",
  title: "Wurzelvorhänge",
  description: "Fachgerechte Erstellung von Wurzelvorhängen zum Schutz des Feinwurzelsystems bei unvermeidbaren Grabungsarbeiten."
}, {
  categoryId: "baumschutz",
  title: "Stamm- & Kronenschutz",
  description: "Installation von mechanischen Schutzeinrichtungen, um Anfahrschäden durch Baumaschinen zuverlässig zu verhindern."
}, {
  categoryId: "gutachten",
  title: "Baumbestandspläne",
  description: "Detaillierte Kartierung und Zustandsbewertung Ihres Baumbestandes als rechtssichere Grundlage für Bauanträge."
}, {
  categoryId: "gutachten",
  title: "Baumbestandspläne",
  description: "Detaillierte Kartierung und Zustandsbewertung Ihres Baumbestandes als rechtssichere Grundlage für Bauanträge."
}, {
  categoryId: "gutachten",
  title: "Sanierungskonzepte",
  description: "Entwicklung von Maßnahmen zur Vitalitätssteigerung nach baulichen Eingriffen für den langfristigen Erhalt Ihrer Bäume."
}];
function CategoryRow({
  category,
  index,
  allServices
}: {
  category: typeof categories[0];
  index: number;
  allServices: typeof services;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.15
  });
  const categoryServices = allServices.filter(s => s.categoryId === category.id);
  return <div ref={ref} data-index={index} className={cn("grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-24 pt-16 md:pt-24 mt-16 md:mt-24 border-t border-border/40 first:border-0 first:pt-0 first:mt-0 transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
            
            <div className="lg:col-span-12 flex flex-col">
                {categoryServices.map((service, sIndex) => <div key={sIndex} data-index={sIndex} className="group relative py-8 first:pt-0 last:pb-0 cursor-pointer">
                        <div className="flex items-start justify-between gap-6">
                            <div className="flex-1">
                                <h4 className="text-2xl font-default font-semibold text-foreground transition-colors duration-300 group-hover:text-primary">
                                    {service.title}
                                </h4>
                                {service.description && <p className="text-muted-foreground mt-3 text-lg leading-relaxed max-w-2xl font-default">
                                        {service.description}
                                    </p>}
                            </div>
                            <div className="mt-1 opacity-0 -translate-x-4 transition-all duration-300 ease-out group-hover:opacity-100 group-hover:translate-x-0 shrink-0">
                                <ArrowRight className="size-6 text-primary" />
                            </div>
                        </div>
                        {sIndex < categoryServices.length - 1 && <Separator className="mt-8 bg-border/40 transition-colors duration-300 group-hover:bg-primary/20" />}
                    </div>)}
            </div>

        </div>;
}
export default function ComplexServiceOffer() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="253185" id="constructionsupervision" className="py-24 md:py-32 bg-background text-foreground overflow-hidden">
            <div className="page-container">
                
                <div ref={headerRef} className={cn("flex flex-col max-w-4xl mb-20 md:mb-32 transition-all duration-1000 ease-out", isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
                    <div className="flex items-center gap-4 mb-6">
                        <Separator className="w-8 bg-primary/60" />
                        <span className="text-xs font-default font-bold tracking-[0.2em] text-primary uppercase">
                            Baubegleitung & Schutz
                        </span>
                    </div>
                    
                    <h2 className="text-4xl md:text-5xl lg:text-[64px] leading-[1.1] font-default font-bold tracking-tight text-foreground">
                        Baumschutz endet nicht beim Bauzaun.
                    </h2>
                    
                    <div className="text-lg md:text-xl text-muted-foreground mt-8 max-w-3xl leading-relaxed font-default space-y-6">
                        <p>
                            Die meisten Baumschäden entstehen während der Bauausführung – durch Zeitdruck, Missverständnisse oder fehlende Abstimmung auf der Baustelle. Dabei sind Schutzmaßnahmen bei Arbeiten im Umfeld von Bäumen häufig nicht nur sinnvoll, sondern nach den anerkannten Regeln der Technik (z. B. DIN 18920 – Schutz von Bäumen bei Baumaßnahmen) auch klar geregelt.
                        </p>
                        <p>
                            Unsere baumschutzfachliche Baubegleitung sorgt dafür, dass diese Vorgaben nicht nur auf dem Papier stehen, sondern auf der Baustelle tatsächlich umgesetzt werden. Wir begleiten sensible Bauphasen direkt vor Ort, überwachen Arbeiten im Wurzelbereich und stimmen uns mit Bauleitung sowie ausführenden Firmen ab.
                        </p>
                        <p>
                            So lassen sich irreversible Baumschäden, Baustopps, Konflikte mit Behörden und unnötige Folgekosten vermeiden.
                        </p>
                    </div>
                </div>

                
                <div className="flex flex-col">
                    {categories.map((category, index) => <CategoryRow key={category.id} index={index} category={category} allServices={services} />)}
                </div>
            </div>
        </section>;
}